import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, Text, View } from 'react-native';

import BrandedButton from '@/components/buttons/BrandedButton';
import CheckboxInputField from '@/components/inputs/CheckboxInputField';
import DatePickerInputField from '@/components/inputs/DateInputPickerField';
import GoogleAutoComplete from '@/components/inputs/GoogleAutocomplete';
import TextInputField from '@/components/inputs/TextInputField';
import SaveAndContinueLaterButton from '@/components/npo/onboarding/SaveAndContinueLaterButton';
import { OwnerData, ownerResolver } from '@/lib/validations/NpoPaymentOnboarding';
import { parseAddressComponents } from '@/utils/addressFromAddressComponents';
import { i18n } from '@/utils/i18n';

import PhoneInput from '../../inputs/PhoneInput';

interface OwnerFormProps {
  initialData?: OwnerData;
  onDone?: (data: OwnerData) => void;
  onSaveAndContinue?: (data: OwnerData) => void;
  doneLabel?: string;
  isLoading?: boolean;
}

const OwnerForm = ({
  initialData,
  onDone,
  onSaveAndContinue,
  doneLabel = i18n.t('mix.done'),
  isLoading = false
}: OwnerFormProps) => {
  const {
    control,
    handleSubmit,
    setValue,
    resetField,
    setError,
    setFocus,
    reset,
    trigger,
    formState: { isValid, errors, isDirty }
  } = useForm<OwnerData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: ownerResolver as any
  });

  const onSubmit: SubmitHandler<OwnerData> = async (data) => {
    if (onDone) {
      onDone(data);
    }
  };

  const onSaveAndContinueHandler: SubmitHandler<OwnerData> = async (data) => {
    if (onSaveAndContinue) {
      onSaveAndContinue(data);
    }
  };

  useEffect(() => {
    if (initialData && !isDirty) {
      reset(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  return (
    <View style={styles.formContainer}>
      <View style={styles.inputsContainer}>
        <TextInputField
          name="firstName"
          control={control}
          placeholder={i18n.t('npoPaymentOnboarding.owner_form.firstName_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          label={i18n.t('npoPaymentOnboarding.owner_form.firstName_label')}
          onSubmitEditing={() => setFocus('middleName')}
        />

        <TextInputField
          name="middleName"
          control={control}
          placeholder={i18n.t('npoPaymentOnboarding.owner_form.middleName_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          label={i18n.t('npoPaymentOnboarding.owner_form.middleName_label')}
          onSubmitEditing={() => setFocus('lastName')}
        />

        <TextInputField
          name="lastName"
          control={control}
          placeholder={i18n.t('npoPaymentOnboarding.owner_form.lastName_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          label={i18n.t('npoPaymentOnboarding.owner_form.lastName_label')}
          onSubmitEditing={() => setFocus('emailAddress')}
        />

        <View style={styles.autocomplete}>
          <GoogleAutoComplete
            initialValue={initialData?.address}
            type="address"
            predictionsScrollEnabled={false}
            error={errors.address}
            label={i18n.t('npoPaymentOnboarding.owner_form.address_label')}
            placeholder={i18n.t('npoPaymentOnboarding.owner_form.address_placeholder')}
            onChange={(result) => {
              if (!result?.postalCode || result.postalCode === '' || !result.city || result.city === '') {
                setError('address', {
                  message: i18n.t('mix.enter_valid_address')
                });
              } else if (result) {
                const components = parseAddressComponents(result.addressComponents);
                setValue(
                  'address',
                  `${components.street}, ${components.city}, ${components.state} ${components.postalCode}, ${components.country}`
                );
                trigger('address');
              } else {
                resetField('address');
                setError('address', {
                  type: 'required',
                  message: i18n.t('npoPaymentOnboarding.owner_form.address_required')
                });
              }
            }}
          />
        </View>

        <TextInputField
          name="emailAddress"
          control={control}
          placeholder={i18n.t('npoPaymentOnboarding.owner_form.emailAddress_placeholder')}
          type="email-address"
          returnKeyLabel="next"
          returnKeyType="next"
          label={i18n.t('npoPaymentOnboarding.owner_form.emailAddress_label')}
          onSubmitEditing={() => setFocus('phone')}
        />

        <View style={styles.phoneInputContainer}>
          <PhoneInput
            initialValue={initialData?.phone}
            label={i18n.t('npoPaymentOnboarding.owner_form.phone_label')}
            placeholder={i18n.t('npoPaymentOnboarding.owner_form.phone_placeholder')}
            error={errors.phone}
            onNumberChange={(number) => {
              if (number) {
                const fullNumber = `${number?.getNationalNumber()}`;
                setValue('phone', fullNumber);
                trigger('phone');
              } else {
                resetField('phone');
                setError('phone', {
                  type: 'required',
                  message: i18n.t('mix.valid_phone_number')
                });
              }
            }}
          />
        </View>

        <DatePickerInputField
          name="dateOfBirth"
          control={control}
          label={i18n.t('npoPaymentOnboarding.owner_form.dateOfBirth_label')}
          timePicker={false}
        />

        <TextInputField
          name="socialSecurityNumber"
          control={control}
          placeholder={i18n.t('npoPaymentOnboarding.owner_form.socialSecurityNumber_placeholder')}
          type="numeric-string"
          returnKeyLabel="next"
          returnKeyType="next"
          label={i18n.t('npoPaymentOnboarding.owner_form.socialSecurityNumber_label')}
          onSubmitEditing={() => setFocus('driversLicenseNumber')}
        />
        <TextInputField
          name="driversLicenseNumber"
          control={control}
          placeholder={i18n.t('npoPaymentOnboarding.owner_form.driversLicenseNumber_placeholder')}
          type="numeric-string"
          returnKeyLabel="next"
          returnKeyType="next"
          label={i18n.t('npoPaymentOnboarding.owner_form.driversLicenseNumber_label')}
        />

        <CheckboxInputField
          name="signingManager"
          control={control}
          label={i18n.t('npoPaymentOnboarding.owner_form.signingManager_label')}
        />
      </View>
      {errors.root !== null && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

      <BrandedButton style={styles.button} loading={isLoading} onPress={handleSubmit(onSubmit)} disabled={!isValid}>
        {doneLabel}
      </BrandedButton>
      <SaveAndContinueLaterButton onPress={handleSubmit(onSaveAndContinueHandler)} />
    </View>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    flex: 1,
    justifyContent: 'space-between'
  },

  inputsContainer: {
    gap: 15
  },

  autocomplete: {
    zIndex: 9999
  },

  phoneInputContainer: {
    zIndex: 10
  },

  button: {
    marginTop: 15
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  }
});

export default OwnerForm;
